import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { APP_VERSION, ClockService, Complaint, ComplaintSparePart, ProductsList, State } from '@icc/b2b/shared';
import { DIALOG_DATA, DialogRef } from '@icc/ui';
import { ProductsService } from 'libs/b2b/trade/src/lib/products.service';
import { LoaderService, SyncService, getSelectedComplaint } from '@icc/b2b/sales-document';
import { HttpClient } from '@angular/common/http';
import { ConfigService, core, logger, PricesDataService } from '@icc/shared';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { ComplaintService } from '../complaint.service';
import { NgxPermissionsService } from 'ngx-permissions';

@Component({
    selector: 'icc-complaint-spare-parts-dialog',
    templateUrl: './complaint-spare-parts-dialog.component.html',
    styleUrls: ['./complaint-spare-parts-dialog.component.scss'],
})
export class ComplaintSparePartsDialogComponent implements OnInit, OnDestroy {

    /**
     * When true, the dialog is blocked because external changes were detected.
     */
    isBlocked = false;

    /**
     * Flag indicating that a permission-affecting change was detected externally.
     * When true, the next store update will trigger a blocking check.
     */
    private pendingBlockCheck = false;

    /**
     * Timestamp when the dialog was opened.
     */
    private dialogOpenedAt = 0;

    /**
     * Cached value of whether user has manufacturer permission.
     * Loaded once on init since permissions don't change during dialog lifetime.
     */
    private hasManufacturerPermission = false;

    private subscriptions: Subscription[] = [];

    productId = 0;
    productsList!: ProductsList;
    allProductsList!: ProductsList;

    complaintSparePartsStocks: any[] = [];
    invalidSpareParts = false;

    complaintSpareParts: (ComplaintSparePart & {
        invalidQuantity?: boolean;
        maxQuantity?: number;
    })[] = [];

    constructor(
        private dialogRef: DialogRef,
        @Inject(DIALOG_DATA)
        public data: {
            complaint: Complaint;
        },
        private productsService: ProductsService,
        private loaderService: LoaderService,
        private http: HttpClient,
        private configService: ConfigService,
        @Inject(APP_VERSION) private appVersion: string,
        private pricesDataService: PricesDataService,
        private store: Store<State>,
        private complaintService: ComplaintService,
        private syncService: SyncService,
        private permissionsService: NgxPermissionsService,
        private cdr: ChangeDetectorRef,
    ) {}

    async ngOnInit() {
        this.dialogOpenedAt = Date.now();

        // Cache manufacturer permission - doesn't change during dialog lifetime
        this.hasManufacturerPermission = await this.permissionsService.hasPermission('complaints-manage-manufacturer');

        this.setupExternalChangesDetection();

        await this.loadData();
        await this.getRemoteComplaintSparePartsStocks();
        await this.updateProductList();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    /**
     * Sets up detection of external changes.
     * Blocks the dialog only if an external change causes loss of edit permission.
     */
    private setupExternalChangesDetection(): void {
        // Fields that affect edit permission
        const permissionFields = [
            'assignedTo',
            'distributorStatusId',
            'manufacturerStatusId',
            'authorizedRetailerStatusId',
        ];

        this.subscriptions.push(
            // Subscribe to external changes - set flag when permission-affecting changes detected
            this.syncService.externalChanges$.subscribe(({
                documentIds,
                documentFields,
                timestamp
            }) => {
                // Ignore changes that happened before dialog was opened
                if (timestamp <= this.dialogOpenedAt) {
                    return;
                }

                // Check if this complaint was modified
                if (!documentIds.has(this.data.complaint.uuid)) {
                    return;
                }

                // If permission-affecting field changed, check immediately
                if (permissionFields.some(field => documentFields.has(field))) {
                    logger.log('[ComplaintSparePartsDialog] Permission field changed externally');
                    this.pendingBlockCheck = true;
                    // Check immediately with current data
                    if (this.isStatusClosed() || !this.currentStatusAllowEdit() || !this.isUserAssigned()) {
                        logger.log('[ComplaintSparePartsDialog] BLOCKING - external change removed permission');
                        this.isBlocked = true;
                        this.cdr.detectChanges();
                    }
                }
            }),

            // Subscribe to complaint document changes - update data and check blocking if needed
            this.store.select(getSelectedComplaint).subscribe((complaint) => {
                if (complaint && complaint.uuid === this.data.complaint.uuid) {
                    Object.assign(this.data.complaint, complaint);

                    // If we have a pending block check, perform it now with updated data
                    if (this.pendingBlockCheck && !this.isBlocked) {
                        if (this.isStatusClosed() || !this.currentStatusAllowEdit() || !this.isUserAssigned()) {
                            logger.log('[ComplaintSparePartsDialog] BLOCKING - external change removed permission');
                            this.isBlocked = true;
                            this.cdr.detectChanges();
                        }
                    }
                }
            }),
        );
    }

    /**
     * Checks if the complaint status is closed.
     */
    private isStatusClosed(): boolean {
        const userType = this.complaintService.getUserType();
        const statusId = this.complaintService.getDocumentStatusForUser(this.data.complaint, userType);
        const status = this.complaintService.statuses.find((s) => s.id === statusId);
        return status?.closed ?? false;
    }

    /**
     * Checks if the current status allows editing.
     */
    private currentStatusAllowEdit(): boolean {
        const userType = this.complaintService.getUserType();
        const statusId = this.complaintService.getDocumentStatusForUser(this.data.complaint, userType);

        if (statusId === 0 || isNaN(statusId)) {
            return true;
        }

        const status = this.complaintService.statuses.find((s) => s.id === statusId);
        return status?.allowEdit ?? false;
    }

    /**
     * Checks if the current user is the assigned party or has manufacturer permission.
     * Users with 'complaints-manage-manufacturer' permission bypass the assignment check.
     */
    private isUserAssigned(): boolean {
        if (this.hasManufacturerPermission) {
            return true;
        }
        const userType = this.complaintService.getUserType();
        return this.data.complaint.assignedTo?.startsWith(userType) ?? false;
    }

    private async loadData() {
        await this.productsService.loadData().then(() =>
            this.productsService.getData().then((productData) => {
                this.allProductsList = productData.complaintProductsList;
            })
        );
    }

    handleSparePart($event) {
        this.productId = $event;
    }

    getProductName(productId: number) {
        return this.allProductsList.find(product => product.id === productId)?.name;
    }

    getUnits(productId: number) {
        const priceUnits = this.allProductsList.find(product => product.id === productId)?.price.priceUnits;
        const units: { id: number; code: string; name: string }[] = [];
        for (const unit of priceUnits || []) {
            const unitName = this.pricesDataService.getUnitInfo(unit.id, unit.code)?.name || '';
            units.push({
                id: unit.id,
                code: unit.code,
                name: unitName,
            });
        }
        return units;
    }

    handleUnit($event: string, sparePart: ComplaintSparePart) {
        const [unitId, unitCode] = $event.split(':');
        sparePart.unitId = Number(unitId) ?? 0;
        sparePart.unitCode = unitCode ?? '';
    }

    addSparePartToComplaint() {
        const product = this.allProductsList.find(prod => prod.id === this.productId);
        if (product) {
            const stock = this.complaintSparePartsStocks.find(el => el.productId === product.id);
            if (!stock?.stock || stock.stock < 1) {
                return;
            }
            const unitId = this.complaintSparePartsStocks.find(el => el.productId === product.id)?.unitId ?? product.price.priceUnits[0].id ?? 0;
            const unitCode = this.complaintSparePartsStocks.find(el => el.productId === product.id)?.unitCode ?? product.price.priceUnits[0].code ?? '';
            const complaintSparePart = {
                uuid: core.generateUUID(),
                productId: this.productId,
                name: product.name,
                quantity: 1,
                unitId,
                unitCode,
                documentId: this.data.complaint.uuid,
                machineId: ClockService.getClock().timestamp.node(),
                companyId: Number(this.configService?.user?.user?.ActiveCompany),
                maxQuantity: stock.stock,
            };
            this.complaintSpareParts.push(complaintSparePart);
            this.productId = 0;
            this.updateProductList();
        }
    }

    async getRemoteComplaintSparePartsStocks() {
        this.loaderService.setLoadingAfterTime();
        const serverComplaintSparePartsStocks = await this.http.post<{
            complaintSpareParts: any[],
        }>(`${window.location.origin}/api/b2b/spare-parts-stock`, {
            userId: this.configService?.user?.user?.id,
            appVersion: this.appVersion,
        }).toPromise();
        if (serverComplaintSparePartsStocks?.complaintSpareParts) {
            this.complaintSparePartsStocks = serverComplaintSparePartsStocks.complaintSpareParts;
        }
        this.loaderService.setLoaded();
    }

    updateProductList() {
        this.productsList = this.allProductsList.filter(product =>
            this.complaintSparePartsStocks.some(stock => stock.productId === product.id && stock.stock > 0)
        );
        this.productsList = this.productsList.filter(product => !this.complaintSpareParts.some(part => part.productId === product.id));
    }

    removeSparePartFromList(complaintSparePart: ComplaintSparePart) {
        this.complaintSpareParts = this.complaintSpareParts.filter(part => part.uuid !== complaintSparePart.uuid);
        this.updateProductList();
    }

    changedQuantity(complaintSparePart: ComplaintSparePart & { invalidQuantity?: boolean }) {
        const stock = this.complaintSparePartsStocks.find(el => el.productId === complaintSparePart.productId);
        if (stock) {
            complaintSparePart.invalidQuantity = complaintSparePart.quantity > stock.stock;
            this.invalidSpareParts = this.complaintSpareParts.some(part => part.invalidQuantity);
        }
    }

    async save() {
        await this.getRemoteComplaintSparePartsStocks();
        this.complaintSpareParts.forEach(part => {
            const stock = this.complaintSparePartsStocks.find(el => el.productId === part.productId);
            part.invalidQuantity = stock && part.quantity > stock.stock;
        });
        const validParts = this.complaintSpareParts.every(part => !part.invalidQuantity);
        if (validParts) {
            this.complaintSpareParts.forEach(part => {
                delete part.invalidQuantity;
                delete part.maxQuantity;
            });
            this.dialogRef.close(this.complaintSpareParts);
            this.invalidSpareParts = false;
        } else {
            this.invalidSpareParts = true;
        }
    }

    close() {
        this.dialogRef.close();
    }
}
